import { Spreadsheet } from '@progress/kendo-react-spreadsheet';
import "@progress/kendo-theme-default/dist/all.css";
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { Box } from '@mui/material';
import { toast } from 'react-toastify';
import { fetchFilteredRevenueShare, postUpdateRevenueShare } from '../../slices/revenue.slice';
import CustomButton from '../../components/Button';
import styles from './Revenue.module.scss';
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import { RevenueComponentProps } from '../../types/revenue';

export const RevenueSpreadsheetComponent: React.FC<RevenueComponentProps> = ({
  data,
  cropOptions,
  landlordOptions,
  selectedYear,
  onSaveSuccess
}) => {
    const [spreadSheetKey, setSpreadSheetKey] = useState<string | undefined>(new Date().toISOString());
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const sheetRef = useRef<any>(null);
    const isUpdatedRevenueShare = useRef(false);
    const [resultArray, setResultArray] = useState<any[]>([]);
    const originalCellValue = useRef<Map<string, number>>(new Map());
    const dispatch = useAppDispatch();

    const handleCancel = () => {
        setShowCancelDialog(true);
    }

    useEffect(() => {
        setSpreadSheetKey(new Date().toISOString())
        setResultArray([]);
        originalCellValue.current.clear();
    }, [data])

    const handleSaveDialogClose = () => setShowSaveDialog(false);
    const handleCancelDialogClose = () => setShowCancelDialog(false);

    const handleSaveYes = () => {
        setShowSaveDialog(false);
        handleSave();
    };

    const handleCancelYes = () => {
        setShowCancelDialog(false);
        dispatch(fetchFilteredRevenueShare({
            year: selectedYear,
            cropTypeIds: cropOptions.map(x => x.id),
            landlordIds: landlordOptions.map((x) => x.id)
        }));
    };

    const onClickSave = () => {
        setShowSaveDialog(true);
    }

    const handleSave = async () => {
        if (resultArray.length === 0) {
            toast.warn("No changes are made!");
            return;
        }

        const result = await dispatch(postUpdateRevenueShare(resultArray));
        if (result?.meta?.requestStatus === "fulfilled") {
            toast.success("Revenue data updated successfully.");
            setResultArray([])
            onSaveSuccess?.();
        }
        else if (result?.meta?.requestStatus === "rejected") {
            const errorMessage = result?.payload || "Unable to update Revenue Data";
            toast.error(errorMessage)
            setResultArray([])
        }
        else {
            toast.error("Something went wrong.")
        }
    }

    const updateRecordInExcel = (rows: any[] | undefined, revenueShare: number, ri: number, ci: number) => {
        let object = {
            Year: selectedYear,
            RevenueShare: 0,
            CropTypeId: 0,
            LandLordPartnerMappingId: 0
        };

        rows !== undefined && rows.forEach(row => {
            const rowIndex = row.index;
            object["RevenueShare"] = revenueShare;
            const cells = row.cells;

            if (ri === rowIndex) {
                cells.forEach((item: any) => {
                    const columnIndex = item.index;
                    
                    if (columnIndex === ci && item.enable === true) {
                        item.value = revenueShare;
                        
                        object["CropTypeId"] = item.operationProductCategoryId || 0;
                        object["LandLordPartnerMappingId"] = item.landLordPartnerMappingId || 0;
                        object["RevenueShare"] = revenueShare;
                    }
                })
            }
        })
        return object
    }

    const handleSelectedCellValue = (e: any) => {
        const range = e.range;
        const row = range._ref.row;
        const col = range._ref.col;
        const cellKey = `${row}-${col}`;
        const workbook = sheetRef.current?.workbook;
        const sheet = workbook?.activeSheet();
        if (!sheet) return;

        const cell = sheet.range(row, col);
        const currentValue = (cell.value() ?? 0) * 100;

        if (!originalCellValue.current.has(cellKey)) {
            originalCellValue.current.set(cellKey, currentValue);
        }
    };

    const handleCellValueChange = (e: any) => {
        if (isUpdatedRevenueShare.current || !sheetRef.current) return;

        const range = e.range;
        const row = range._ref.topLeft.row;
        const col = range._ref.topLeft.col;
        const cellKey = `${row}-${col}`;
        let value = range.value();
        const data = e.sender.options.sheets;

        if (typeof value !== "string") {
            value = String(value);
        }
        value = value.trim();
        
        let extractedValue = value.match(/^\d+(\.\d+)?%?$/) ? value.replace("%", "") : NaN;
        let cellValue = parseFloat(extractedValue);
        const originalValue = originalCellValue.current.get(cellKey) ?? 0;
        
        if (isNaN(cellValue) || cellValue < 0 || cellValue > 100) {
            toast.error("Invalid Revenue Share. Please enter a value between 0-100.");
            cellValue = originalValue;
        } else {
            originalCellValue.current.set(cellKey, cellValue);
        }

        isUpdatedRevenueShare.current = true;

        try {
            const workbook = sheetRef.current.workbook;
            const sheet = workbook.activeSheet();
            if (sheet) {
                const cell = sheet.range(row, col);
                cell.value(cellValue / 100);
                cell.format("0.00%");
            }

            if (data !== undefined) {
                if (cellValue !== originalValue) {
                    const cellData = updateRecordInExcel(data[0].rows, cellValue, row, col);

                    if (cellData.CropTypeId === 0 || cellData.LandLordPartnerMappingId === 0) {
                        console.error('Missing required IDs:', cellData);
                        toast.error('Unable to identify crop type or landlord mapping for this cell');
                        return;
                    }

                    setResultArray((prevData) => {
                        const updatedCellValue = prevData.filter(item =>
                            item.CropTypeId !== cellData.CropTypeId ||
                            item.LandLordPartnerMappingId !== cellData.LandLordPartnerMappingId
                        );

                        updatedCellValue.push({
                            Year: cellData.Year,
                            CropTypeId: cellData.CropTypeId,
                            LandLordPartnerMappingId: cellData.LandLordPartnerMappingId,
                            RevenueShare: cellData.RevenueShare
                        });

                        return updatedCellValue;
                    });
                }
            }
        }
        catch (error) {
            console.log(error);
        }
        finally {
            isUpdatedRevenueShare.current = false;
        }
    };

    return (
        <div>
            <ConfirmationDialog
                open={showSaveDialog}
                title="Save"
                description="Changes to revenue sharing data will impact revenue calculations. Do you want to save the changes?"
                handleNo={handleSaveDialogClose}
                handleYes={handleSaveYes}
            />

            <ConfirmationDialog
                open={showCancelDialog}
                title="Warning"
                description="Are you sure you want to cancel? Any unsaved changes will be lost."
                handleNo={handleCancelDialogClose}
                handleYes={handleCancelYes}
            />

            <Spreadsheet
                className={styles.spreadsheet}
                key={spreadSheetKey}
                ref={sheetRef}
                toolbar={false}
                style={{
                    width: "100%",
                    height: "80vh",
                }}
                onChange={handleCellValueChange}
                onSelect={handleSelectedCellValue}
                defaultProps={data}
            />
            
            <Box
                sx={{ display: "flex", justifyContent: "center", gap: "1rem" }}
            >
                <button onClick={handleCancel} className={styles.cancelButton}>
                    Cancel
                </button>
                <CustomButton
                    onClick={onClickSave}
                    disabled={!resultArray.length}
                    sx={{ width: "110px" }}
                >
                    Save
                </CustomButton>
            </Box>
        </div>
    );
}